// Taken from langchainjs
// https://github.com/langchain-ai/langchainjs/blob/main/langchain/src/text_splitter.ts#L289

export const SEPARATORS = Object.freeze(['\n\n', '\n', ' ', ''])
export const CHUNK_OVERLAP_RATIO = 0.2

function mergeSplits({
  splits,
  separator,
  maxFragmentSize,
  lengthFunction,
}: {
  splits: string[]
  separator: string
  maxFragmentSize: number
  lengthFunction: (s: string) => number
}): string[] {
  const docs: string[] = []
  const currentDoc: string[] = []
  let total = 0
  const chunkOverlap = Math.floor(maxFragmentSize * CHUNK_OVERLAP_RATIO)
  for (const d of splits) {
    const _len = lengthFunction(d)
    if (total + _len + (currentDoc.length > 0 ? 1 : 0) > maxFragmentSize) {
      if (currentDoc.length > 0) {
        const doc = currentDoc.join(separator).trim()
        if (doc) {
          docs.push(doc)
        }

        while (
          currentDoc[0] &&
          (total > chunkOverlap ||
            (total + _len > maxFragmentSize && total > 0))
        ) {
          total -= lengthFunction(currentDoc[0])
          currentDoc.shift()
        }
      }
    }
    currentDoc.push(d)
    total += _len
  }
  const doc = currentDoc.join(separator).trim()
  if (doc) {
    docs.push(doc)
  }
  return docs
}

export function recursiveCharacterTextSplitter({
  text,
  separators = SEPARATORS,
  maxFragmentSize,
  lengthFunction,
}: {
  text: string
  maxFragmentSize: number
  lengthFunction: (s: string) => number
  separators?: readonly string[]
}): string[] {
  const finalChunks: string[] = []

  let separator = ''
  let newSeparators: string[] = []
  for (let i = 0; i < separators.length; i++) {
    const s = separators.at(i) ?? ''
    if (s === '') {
      separator = s
      break
    }
    if (text.includes(s)) {
      separator = s
      newSeparators = SEPARATORS.slice(i + 1)
      break
    }
  }

  const splits = text.split(separator).filter((s) => s !== '')

  // Now go merging things, recursively splitting longer texts.
  let goodSplits: string[] = []
  for (const s of splits) {
    if (lengthFunction(s) < maxFragmentSize) {
      goodSplits.push(s)
    } else {
      if (goodSplits.length) {
        const mergedText = mergeSplits({
          maxFragmentSize,
          splits: goodSplits,
          separator,
          lengthFunction,
        })
        finalChunks.push(...mergedText)
        goodSplits = []
      }
      if (newSeparators.length === 0) {
        finalChunks.push(s)
      } else {
        const otherInfo = recursiveCharacterTextSplitter({
          maxFragmentSize,
          text: s,
          separators: newSeparators,
          lengthFunction,
        })
        finalChunks.push(...otherInfo)
      }
    }
  }
  if (goodSplits.length) {
    const mergedText = mergeSplits({
      splits: goodSplits,
      separator,
      maxFragmentSize,
      lengthFunction,
    })
    finalChunks.push(...mergedText)
  }
  if (finalChunks.length === 0) {
    finalChunks.push('')
  }
  return finalChunks
}
